import { useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { LABELS, type CyclePhase, type DdBasis } from "@match/shared";
import { AccountConflictNote } from "@/components/account-conflict";
import { showSymbolWarnings } from "@/components/notice";
import { PHASE_LABEL } from "@/components/session";
import { SymbolPanel, usePeersOf } from "@/components/symbols";
import { FormCheck, MissingNote, useFormCheck, type FieldRule } from "@/components/forms";
import { Button, CheckArea, CheckScope, DecimalField, ErrorNote, Field, SidePanel, inputClass, scopedCheck } from "@/components/ui";
import { ApiError, api, errorMessage } from "@/lib/api";
import { dateTime, money, percent } from "@/lib/format";
import { useBrokers, useCyclesAwaitingAccount, usePropFirms, useRefreshDirectory, useServers, useSession, useSessionsAwaitingProp } from "@/lib/queries";
import { decimalInput, fractionToPct, parseDecimal, pctIssue, pctToFraction } from "@/lib/units";
import type { Account, AccountConflict, BrokerServer, CycleAwaitingAccount, PropFirm, SessionAwaitingProp } from "@/lib/types";

/*
 * IL PERCORSO CICLO-FASI, UNICO E GUIDATO, DALLA SCHEDA CLIENTE.
 *
 * Un solo pulsante — «Aggiungi conto» — invece di tre porte separate
 * (`/fasi/nuova`, `/cycles/:id/next-account` chiuso dietro un curl, e niente
 * per il broker che arriva dopo). Si dichiara SEMPRE la fase (Fase 1, Fase 2,
 * Funded) e SEMPRE la prop firm col suo programma.
 *
 * La casella «Contabilità / ciclo» è SPENTA per difetto: senza, la Fase opera da
 * sola — nessun ciclo, nessun periodo, nessun registro — e il ciclo si crea
 * dopo, a comando, da una Fase 1 («Crea ciclo»). Con la casella accesa la Fase 1
 * apre un ciclo nuovo, e Fase 2 e Funded continuano quello che aspetta il conto:
 * mai una combinazione che il backend rifiuterebbe.
 *
 * Il broker è la terza scelta indipendente: libero fra quelli del cliente,
 * nuovo, o nessuno. Senza broker la Fase nasce lo stesso, `AWAITING_BROKER`
 * — si aggancia più tardi dalla scheda della Fase (`AttachBrokerPanel`).
 *
 * PRIMA DI TUTTO si sceglie che tipo di conto è. Prop = il percorso qui sopra.
 * Broker = solo il conto (login, password, server, valuta, etichetta): nessuna
 * Fase, nessun ciclo; resta fra i broker liberi del cliente, pronto per una Fase.
 */

type AccountKind = "PROP" | "BROKER";

const BASES = Object.keys(LABELS.ddBasis) as DdBasis[];

const cicloAttesaLabel = (c: CycleAwaitingAccount) =>
  `${PHASE_LABEL(c.awaitingPropPhase)} · ${c.label ?? c.propFirm.name} · ${LABELS.terms.recoverAmount} ${money(c.recoverAmount, c.currency)}`;

const PHASES: CyclePhase[] = ["PHASE_1", "PHASE_2", "FUNDED"];

const sessioneAttesaLabel = (f: SessionAwaitingProp) =>
  `${PHASE_LABEL(f.awaitingPropPhase)} · ${f.preset ? `${f.preset.propFirm.name} ${f.preset.name}` : "senza programma"} · senza contabilità`;

/** Il target di fase del programma, per la fase dichiarata. */
const targetOf = (p: { phase1TargetPct: string | null; phase2TargetPct: string | null; fundedTargetPct: string | null }, phase: CyclePhase) =>
  phase === "PHASE_1" ? p.phase1TargetPct : phase === "PHASE_2" ? p.phase2TargetPct : p.fundedTargetPct;

export function NewPropAccountPanel({
  open,
  clientId,
  clientName,
  brokerAccounts,
  /** Aperto da "Registra il conto" su un ciclo preciso: il menu parte già lì. */
  defaultTargetCycleId,
  /** Come sopra, per una Fase SENZA ciclo che aspetta il conto. */
  defaultTargetSessionId,
  /**
   * «Nuovo conto» da una Fase bruciata col ciclo che aspetta le credenziali: il
   * pannello di sempre, col broker di prima già scelto e la fase attesa; alla
   * creazione l'attesa si chiude (`fromAwaitingSessionId`).
   */
  fromAwaitingSessionId,
  defaultBrokerAccountId,
  defaultPhase,
  onClose,
}: {
  open: boolean;
  clientId: string;
  clientName: string;
  /** I conti broker del cliente: da qui si sceglie quello "libero fra i miei". */
  brokerAccounts: Account[];
  defaultTargetCycleId?: string;
  defaultTargetSessionId?: string;
  fromAwaitingSessionId?: string;
  defaultBrokerAccountId?: string;
  defaultPhase?: CyclePhase;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const refresh = useRefreshDirectory();
  const servers = useServers();
  const firms = usePropFirms();
  const brokers = useBrokers();
  const awaiting = useCyclesAwaitingAccount({ clientId });
  const awaitingSessions = useSessionsAwaitingProp({ clientId });

  /** Che tipo di conto si crea. Nessuna preselezione, salvo «Registra il conto» su un'attesa: lì è per forza il prop. */
  const [kind, setKind] = useState<AccountKind | null>(defaultTargetCycleId || defaultTargetSessionId || fromAwaitingSessionId ? "PROP" : null);

  // --- Il conto prop nuovo ---
  const [mt5Login, setMt5Login] = useState("");
  const [mt5Password, setMt5Password] = useState("");
  /** Si sceglie PRIMA la prop firm, poi il server: quelli già legati a lei stanno in cima. */
  const [propFirmId, setPropFirmId] = useState("");
  const [propServerId, setPropServerId] = useState("");
  const [propCurrency, setPropCurrency] = useState("USD");
  const [propLabel, setPropLabel] = useState("");

  /*
   * CONTABILITA' / CICLO, SPENTA per difetto. Se il pannello si apre su un ciclo
   * che aspetta il conto, la contabilita' c'e' gia': si parte accesa.
   */
  const [accounting, setAccounting] = useState<boolean>(!!defaultTargetCycleId);
  // --- La fase: "NEW", "cycle:<id>" (un ciclo che aspetta) o "session:<id>" (una Fase senza ciclo che aspetta) ---
  const [target, setTarget] = useState<string>(defaultTargetCycleId ? `cycle:${defaultTargetCycleId}` : defaultTargetSessionId ? `session:${defaultTargetSessionId}` : "NEW");
  // Una scelta che l'altra modalita' non offre non resta selezionata di nascosto: si torna a «nuova».
  const targetOk = target === "NEW" || (accounting ? target.startsWith("cycle:") : target.startsWith("session:"));
  const effTarget = targetOk ? target : "NEW";
  const nuovaChallenge = effTarget === "NEW";
  const cicloScelto = effTarget.startsWith("cycle:") ? (awaiting.data?.find((c) => c.id === effTarget.slice(6)) ?? null) : null;
  const faseScelta = effTarget.startsWith("session:") ? (awaitingSessions.data?.find((f) => f.id === effTarget.slice(8)) ?? null) : null;
  // La fase che si dichiara: con la contabilita', Fase 2 e Funded partono da un ciclo nuovo con le perdite delle fasi fatte fuori.
  const [phaseChoice, setPhaseChoice] = useState<CyclePhase>(defaultPhase ?? "PHASE_1");
  // Le perdite sul broker delle fasi fatte fuori dal gestionale, in valuta del ciclo (0 ammesso, vuoto no).
  const [lossP1, setLossP1] = useState("");
  const [lossP2, setLossP2] = useState("");
  const [lossNote, setLossNote] = useState("");

  // --- Solo per una challenge nuova ---
  const [presetId, setPresetId] = useState("");
  const [accountSize, setAccountSize] = useState("");
  const [fee, setFee] = useState("");
  const [maxDdPct, setMaxDdPct] = useState("");
  const [basis, setBasis] = useState<DdBasis>("STATIC_INITIAL");
  const [cycleLabel, setCycleLabel] = useState("");
  const [dailyDdPct, setDailyDdPct] = useState("");
  const [targetPct, setTargetPct] = useState("");
  const [symbol, setSymbol] = useState("");

  // --- Il broker: libero, nuovo, o nessuno ---
  const [brokerChoice, setBrokerChoice] = useState<"NONE" | "EXISTING" | "NEW">(brokerAccounts.some((a) => !a.liveSession) ? "EXISTING" : "NONE");
  // Il broker di prima, se è ancora libero: proposto, non riservato (se lo ha preso un'altra Fase vale il primo libero).
  const [brokerAccountId, setBrokerAccountId] = useState(
    () => brokerAccounts.find((a) => !a.liveSession && a.id === defaultBrokerAccountId)?.id ?? brokerAccounts.find((a) => !a.liveSession)?.id ?? "",
  );
  const [newBrokerLogin, setNewBrokerLogin] = useState("");
  const [newBrokerPassword, setNewBrokerPassword] = useState("");
  /** Come per il prop: prima il broker, poi il server. Vale per il conto broker da solo e per «Il broker → Nuovo». */
  const [brokerOwnerId, setBrokerOwnerId] = useState("");
  const [newBrokerServerId, setNewBrokerServerId] = useState("");
  const [newBrokerCurrency, setNewBrokerCurrency] = useState("USD");
  const [newBrokerLabel, setNewBrokerLabel] = useState("");

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  /** «Leggi simboli dal conto» in corso: il pulsante resta spento, un doppio clic sarebbe una doppia connessione. */
  const [readingSymbols, setReadingSymbols] = useState(false);
  const [readSymbolsError, setReadSymbolsError] = useState<string | null>(null);
  // Il conto che tiene un numero MT5 gia' usato (409 LOGIN_TAKEN), dal salvataggio o dalla lettura dei simboli: `AccountConflictNote`.
  const [conflict, setConflict] = useState<AccountConflict | null>(null);
  /**
   * «+ Mappa simbolo…»: il form della pagina Server, aperto SOPRA questo pannello
   * e non al suo posto. Questo resta montato sotto, con tutto quello che c'è scritto.
   */
  const [mapOn, setMapOn] = useState<BrokerServer | null>(null);
  const peersOf = usePeersOf();

  const propServers = (servers.data ?? []).filter((s) => s.kind === "PROP");
  const brokerServers = (servers.data ?? []).filter((s) => s.kind === "BROKER");
  const propServer = propServers.find((s) => s.id === propServerId);
  const newBrokerServer = brokerServers.find((s) => s.id === newBrokerServerId);
  const allPresets = (firms.data ?? []).flatMap((f) => f.presets.map((p) => ({ ...p, firmName: f.name })));
  const preset = allPresets.find((p) => p.id === presetId);
  // Continuando un ciclo o una Fase che aspettano il conto la prop firm è la loro: non si cambia a metà.
  const firmDelTarget = cicloScelto?.propFirm.id ?? faseScelta?.preset?.propFirm.id ?? null;
  const firmBloccata = !nuovaChallenge && firmDelTarget !== null;
  const effFirmId = firmBloccata ? firmDelTarget : propFirmId;
  const programmi = (firms.data ?? []).find((f) => f.id === effFirmId)?.presets ?? [];
  const brokerLiberi = brokerAccounts.filter((a) => !a.liveSession);

  const phase: CyclePhase = phaseChoice;
  // Una Fase 2 esiste solo nei programmi a due step: il backend rifiuterebbe (422) e qui non si offre.
  const phase2Available = !preset || preset.phases >= 2;
  // Ciclo nuovo da Fase 2 o Funded: le fasi fatte fuori si dichiarano (Fase 2 solo sui due step).
  const declaresLosses = accounting && phase !== "PHASE_1";
  // Un instant (nessuna fase di valutazione) in Funded non ha perdite da dichiarare: solo la fee.
  const declaresP1 = declaresLosses && !(phase === "FUNDED" && preset?.phases === 0);
  const declaresP2 = declaresLosses && phase === "FUNDED" && !!preset && preset.phases >= 2;

  // Preset o fase scelti: si precompilano taglia, fee, DD e il target di QUELLA fase.
  useEffect(() => {
    if (!preset || !nuovaChallenge) return;
    setAccountSize(String(Number(preset.accountSize)));
    // Uno ZERO del programma non e' una fee: nel catalogo vuol dire «non dichiarata», e precompilato
    // finirebbe nel «Da recuperare» come una challenge gratis. Il campo resta vuoto e obbligatorio.
    // Vale anche cambiando programma: la fee del programma di prima non resta a fare da valore.
    setFee(Number(preset.challengeFee) > 0 ? String(Number(preset.challengeFee)) : "");
    setMaxDdPct(fractionToPct(preset.maxDdPct));
    setBasis(preset.maxDdBasis);
    setDailyDdPct(fractionToPct(preset.dailyDdPct));
    setTargetPct(fractionToPct(targetOf(preset, phase)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetId, phase]);

  // Un programma senza Fase 2 non lascia selezionata una Fase 2.
  useEffect(() => {
    if (phaseChoice === "PHASE_2" && !phase2Available) setPhaseChoice("PHASE_1");
  }, [phaseChoice, phase2Available]);

  const effective = {
    accountSize: decimalInput(accountSize),
    maxDdPct: pctToFraction(maxDdPct),
    fee: decimalInput(fee),
  };

  // Simbolo: solo per una challenge nuova. Con broker scelto (libero o nuovo con
  // server noto), l'elenco è l'intersezione fra i due server — la stessa regola
  // dell'apertura. Senza broker, quello che è mappato sul server prop: il
  // backend lo verifica di nuovo quando il broker arriva.
  const brokerServerForSymbols =
    brokerChoice === "EXISTING" ? servers.data?.find((s) => s.id === brokerAccounts.find((a) => a.id === brokerAccountId)?.serverId) : brokerChoice === "NEW" ? newBrokerServer : undefined;
  const symbolOptions = !propServer
    ? []
    : brokerServerForSymbols
      ? propServer.symbols.map((s) => s.canonical).filter((c) => brokerServerForSymbols.symbols.some((b) => b.canonical === c)).sort()
      : [...propServer.symbols.map((s) => s.canonical)].sort();

  // I campi del conto broker nuovo stanno nel loro ambito (`NewBrokerFields`): «Login MT5 (conto broker)».
  const brokerRules = (on: boolean): FieldRule[] => [
    on && [scopedCheck("Login MT5", BROKER_SCOPE), !!newBrokerLogin.trim()],
    on && [scopedCheck("Password MT5", BROKER_SCOPE), !!newBrokerPassword],
    on && [scopedCheck("Broker", BROKER_SCOPE), !!brokerOwnerId],
    on && [scopedCheck("Server", BROKER_SCOPE), !!newBrokerServerId],
  ];
  const prop = kind === "PROP";
  const nuova = prop && nuovaChallenge;
  const check = useFormCheck([
    ["Tipo di conto", kind !== null],
    ...brokerRules(kind === "BROKER"),
    prop && ["Login MT5", !!mt5Login.trim()],
    prop && ["Password MT5", !!mt5Password],
    prop && ["Prop firm", !!effFirmId],
    prop && ["Server", !!propServerId],
    prop && !nuovaChallenge && ["Continua", !!cicloScelto || !!faseScelta],
    nuova && ["Programma", !!presetId],
    nuova && ["Taglia del conto", !!effective.accountSize],
    nuova && accounting && ["Fee pagata", !!effective.fee],
    nuova && declaresP1 && ["Perdita in Fase 1", decimalInput(lossP1) !== null],
    nuova && declaresP2 && ["Perdita in Fase 2", decimalInput(lossP2) !== null],
    nuova && ["Max drawdown", !!effective.maxDdPct],
    // Un testo non capito nei due campi facoltativi non deve partire come «vuoto».
    nuova && ["DD giornaliero", !pctIssue("DD giornaliero", dailyDdPct)],
    nuova && ["Target", !pctIssue("Target", targetPct)],
    nuova && ["Simbolo", !!symbol],
    prop && brokerChoice === "EXISTING" && ["Conto broker", !!brokerAccountId],
    ...brokerRules(prop && brokerChoice === "NEW"),
  ]);

  // Il pulsante compare solo su un server SENZA simboli mappati e a credenziali compilate.
  const canReadSymbols = kind === "PROP" && nuovaChallenge && !!propServer && propServer.symbols.length === 0 && !!mt5Login.trim() && !!mt5Password;

  const readSymbols = async () => {
    if (readingSymbols || !canReadSymbols) return;
    setReadingSymbols(true);
    setReadSymbolsError(null);
    setConflict(null);
    try {
      await api.servers.readSymbols(propServerId, { clientId, mt5Login: mt5Login.trim(), mt5Password });
      // I server si rileggono: il menu Simbolo si riempie da solo.
      await refresh();
    } catch (e) {
      setReadSymbolsError(errorMessage(e));
      if (e instanceof ApiError && e.conflict) setConflict(e.conflict);
    } finally {
      setReadingSymbols(false);
    }
  };

  const submit = async () => {
    if (!check.attempt()) return;
    setBusy(true);
    setError(null);
    setErrorCode(null);
    setConflict(null);
    try {
      // Un conto broker da solo: nessuna Fase, nessun ciclo. Resta libero, pronto per essere agganciato.
      if (kind === "BROKER") {
        await api.accounts.create({
          label: newBrokerLabel.trim() || `broker ${newBrokerLogin.trim()}`,
          clientId,
          role: "BROKER",
          mt5Login: newBrokerLogin.trim(),
          mt5Password: newBrokerPassword,
          serverId: newBrokerServerId,
          currency: newBrokerCurrency,
        });
        await refresh();
        onClose();
        return;
      }

      /*
       * CONTINUANDO un ciclo o una Fase che aspettano, il prop nasce DENTRO
       * `next-account`, nella sua transazione e dopo i suoi controlli (il simbolo
       * sul server del broker nuovo compreso): un rifiuto non lascia un conto
       * prop creato a metà. La valuta è quella del prop di prima (la decide il backend).
       */
      if (!nuovaChallenge && (cicloScelto || faseScelta)) {
        const propNuovo = { mt5Login: mt5Login.trim(), mt5Password, serverId: propServerId, label: propLabel.trim() || `prop ${mt5Login.trim()}` };
        const body =
          brokerChoice === "EXISTING"
            ? { ...propNuovo, brokerAccountId }
            : brokerChoice === "NEW"
              ? { ...propNuovo, newBroker: { mt5Login: newBrokerLogin.trim(), mt5Password: newBrokerPassword, serverId: newBrokerServerId, label: newBrokerLabel.trim() || null, currency: newBrokerCurrency } }
              : { ...propNuovo, noBroker: true as const };
        const r = cicloScelto ? await api.cycles.nextAccount(cicloScelto.id, body) : await api.sessions.nextAccount(faseScelta!.id, body);
        showSymbolWarnings(r);
        await refresh();
        navigate(`/fasi/${r.session.id}`);
        return;
      }

      /*
       * Il conto prop nasce sempre: è il motivo per cui si è aperto questo
       * pannello. Nasce INSIEME alla Fase, in una richiesta sola (e il broker
       * nuovo con lui): se la Fase non nasce, il backend annulla i conti appena
       * creati. Prima erano tre chiamate, e un rifiuto sull'ultima lasciava i
       * conti orfani.
       */
      const r = await api.sessions.create({
        clientId,
        newPropAccount: {
          label: propLabel.trim() || `prop ${mt5Login.trim()}`,
          mt5Login: mt5Login.trim(),
          mt5Password,
          serverId: propServerId,
          currency: propCurrency,
        },
        ...(brokerChoice === "EXISTING" && brokerAccountId && { brokerAccountId }),
        ...(brokerChoice === "NEW" && {
          newBrokerAccount: {
            label: newBrokerLabel.trim() || `broker ${newBrokerLogin.trim()}`,
            mt5Login: newBrokerLogin.trim(),
            mt5Password: newBrokerPassword,
            serverId: newBrokerServerId,
            currency: newBrokerCurrency,
          },
        }),
        canonicalSymbol: symbol,
        phase,
        presetId,
        // Sempre esplicito: l'API, senza, vale `true`.
        accounting,
        // Etichetta e fee sono del CICLO: senza contabilita' il backend le rifiuterebbe.
        ...(accounting ? { cycleLabel: cycleLabel.trim() || null, challengeFee: effective.fee! } : {}),
        ...(declaresLosses && {
          // Sempre presente con Fase 2 o Funded, anche vuoto (instant): dice «ciclo nuovo da qui».
          declaredLosses: {
            ...(declaresP1 && { PHASE_1: decimalInput(lossP1)! }),
            ...(declaresP2 && { PHASE_2: decimalInput(lossP2)! }),
            ...(declaresP1 && lossNote.trim() && { note: lossNote.trim() }),
          },
        }),
        accountSize: effective.accountSize!,
        maxDdPct: effective.maxDdPct!,
        maxDdBasis: basis,
        dailyDdPct: pctToFraction(dailyDdPct),
        challengeTargetPct: pctToFraction(targetPct),
        ...(fromAwaitingSessionId && { fromAwaitingSessionId }),
      });
      showSymbolWarnings(r);
      await refresh();
      navigate(`/fasi/${r.session.id}`);
    } catch (e) {
      setError(errorMessage(e));
      setErrorCode(e instanceof ApiError ? e.code : null);
      if (e instanceof ApiError && e.conflict) setConflict(e.conflict);
      const broker = (f: string) => scopedCheck(f, BROKER_SCOPE);
      check.fromError(
        e,
        kind === "BROKER"
          ? { mt5Login: broker("Login MT5"), LOGIN_TAKEN: broker("Login MT5"), mt5Password: broker("Password MT5"), serverId: broker("Server") }
          : {
              mt5Login: "Login MT5",
              mt5Password: "Password MT5",
              serverId: "Server",
              "newPropAccount.mt5Login": "Login MT5",
              "newPropAccount.mt5Password": "Password MT5",
              "newPropAccount.serverId": "Server",
              "newBrokerAccount.mt5Login": broker("Login MT5"),
              "newBrokerAccount.mt5Password": broker("Password MT5"),
              "newBrokerAccount.serverId": broker("Server"),
              "newBroker.mt5Login": broker("Login MT5"),
              "newBroker.mt5Password": broker("Password MT5"),
              "newBroker.serverId": broker("Server"),
              brokerAccountId: "Conto broker",
              presetId: "Programma",
              accountSize: "Taglia del conto",
              challengeFee: "Fee pagata",
              "declaredLosses.PHASE_1": "Perdita in Fase 1",
              "declaredLosses.PHASE_2": "Perdita in Fase 2",
              maxDdPct: "Max drawdown",
              dailyDdPct: "DD giornaliero",
              challengeTargetPct: "Target",
              canonicalSymbol: "Simbolo",
            },
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
    <SidePanel
      open={open}
      // Col form del simbolo aperto sopra, Esc chiude quello e non questo: qui c'è un modulo mezzo compilato.
      onClose={mapOn ? () => setMapOn(null) : onClose}
      title={kind === "PROP" ? "Nuovo conto prop" : kind === "BROKER" ? "Nuovo conto broker" : "Aggiungi conto"}
      subtitle={clientName}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Annulla
          </Button>
          <Button variant="primary" disabled={busy} onClick={() => void submit()}>
            {busy ? "Creazione…" : "Crea"}
          </Button>
          <MissingNote check={check} end />
        </>
      }
    >
      <FormCheck check={check}>
      <div className="space-y-5">
        <section className="space-y-3">
          <h3 className="text-sm font-medium text-ink">Che tipo di conto è?</h3>
          <CheckArea name="Tipo di conto">
          <div role="radiogroup" aria-label="Tipo di conto" className="grid grid-cols-2 gap-2">
            {(
              [
                ["PROP", "Conto prop"],
                ["BROKER", "Conto broker"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={kind === value}
                onClick={() => setKind(value)}
                className={`rounded border px-2 py-1.5 text-xs ${kind === value ? "border-accent bg-raised font-medium text-ink" : "border-line text-muted hover:text-ink"}`}
              >
                {label}
              </button>
            ))}
          </div>
          </CheckArea>
          {kind === null && (
            <p className="text-xs text-muted">
              Il prop è la gamba di partenza e porta con sé la fase; il broker è il conto che riceve la gamba opposta.
            </p>
          )}
        </section>

        {kind === "BROKER" && (
          <section className="space-y-3 border-t border-line pt-4">
            <h3 className="text-sm font-medium text-ink">Il conto broker</h3>
            <NewBrokerFields
              brokers={brokers.data ?? []}
              brokerId={brokerOwnerId}
              onBrokerId={setBrokerOwnerId}
              servers={brokerServers}
              login={newBrokerLogin}
              onLogin={setNewBrokerLogin}
              password={newBrokerPassword}
              onPassword={setNewBrokerPassword}
              serverId={newBrokerServerId}
              onServerId={setNewBrokerServerId}
              currency={newBrokerCurrency}
              onCurrency={setNewBrokerCurrency}
              label={newBrokerLabel}
              onLabel={setNewBrokerLabel}
            />
            <p className="rounded-card border border-line bg-surface p-3 text-xs text-muted">
              Nessuna fase e nessun ciclo: il conto entra fra i broker liberi di questo cliente, e si aggancia a una fase quando serve.
            </p>
          </section>
        )}

        {kind === "PROP" && (
        <>
        <section className="space-y-3 border-t border-line pt-4">
          <h3 className="text-sm font-medium text-ink">Il conto prop</h3>
          <Field label="Login MT5">{(id) => <input id={id} value={mt5Login} onChange={(e) => setMt5Login(e.target.value)} className={inputClass} />}</Field>
          <Field label="Password MT5">{(id) => <input id={id} type="password" value={mt5Password} onChange={(e) => setMt5Password(e.target.value)} className={inputClass} />}</Field>
          <OwnerServerFields
            kind="PROP"
            owners={firms.data ?? []}
            ownerId={effFirmId}
            onOwnerId={(v) => {
              setPropFirmId(v);
              // Il programma è di UNA prop firm: cambiando firm non resta selezionato quello di un'altra.
              setPresetId("");
            }}
            ownerLocked={firmBloccata}
            servers={propServers}
            serverId={propServerId}
            onServerId={setPropServerId}
          >
            {nuovaChallenge ? (
              <Field label="Valuta" hint="Verificata alla connessione: se il server ne dice un'altra, un conto senza storico si corregge da solo.">{(id) => <input id={id} value={propCurrency} maxLength={3} onChange={(e) => setPropCurrency(e.target.value.toUpperCase())} className={inputClass} />}</Field>
            ) : (
              <div>
                <p className="text-xs text-faint">Valuta</p>
                <p className="mt-1 text-sm text-muted">Quella del conto prop di prima: il nuovo ne prende il posto.</p>
              </div>
            )}
          </OwnerServerFields>
          <Field label="Etichetta" hint="Facoltativa: se vuota, prop + il login.">
            {(id) => <input id={id} value={propLabel} onChange={(e) => setPropLabel(e.target.value)} className={inputClass} />}
          </Field>
        </section>

        <section className="space-y-3 border-t border-line pt-4">
          <h3 className="text-sm font-medium text-ink">La fase</h3>

          {/*
            * LA CONTABILITA' e' una scelta di questa Fase, spenta per difetto:
            * accesa, nasce un ciclo (fee, recover, periodi, split); spenta,
            * la Fase opera da sola e il ciclo si crea dopo.
            */}
          <label className="flex items-start gap-2 rounded-card border border-line bg-surface p-3 text-sm">
            <input type="checkbox" checked={accounting} onChange={(e) => setAccounting(e.target.checked)} className="mt-0.5" />
            <span>
              <span className="font-medium text-ink">Contabilità / ciclo</span>
              <span className="mt-0.5 block text-xs text-muted">
                {accounting
                  ? "Nasce un ciclo: fee, da recuperare, periodi sul saldo del broker e split. Una Fase 1 apre un ciclo nuovo; Fase 2 e Funded continuano quello che aspetta il conto."
                  : "La fase opera da sola, senza ciclo: niente registro né recover. Il ciclo si crea dopo dal menu della fase («Crea ciclo»), solo da una Fase 1."}
              </span>
            </span>
          </label>

          <Field label="Continua">
            {(id) => (
              <select id={id} value={effTarget} onChange={(e) => setTarget(e.target.value)} className={inputClass}>
                <option value="NEW">{accounting ? "Nuovo ciclo" : "Nuova fase"}</option>
                {accounting
                  ? (awaiting.data ?? []).map((c) => (
                      <option key={c.id} value={`cycle:${c.id}`}>
                        {cicloAttesaLabel(c)}
                      </option>
                    ))
                  : // Solo quelle senza ciclo continuano da qui: una bruciata col ciclo aspetta un ciclo nuovo («Nuovo conto»).
                    (awaitingSessions.data ?? []).filter((f) => f.cycleId === null).map((f) => (
                      <option key={f.id} value={`session:${f.id}`}>
                        {sessioneAttesaLabel(f)}
                      </option>
                    ))}
              </select>
            )}
          </Field>

          {!nuovaChallenge && cicloScelto && (
            <dl className="grid grid-cols-2 gap-3 rounded-card border border-line bg-surface p-4 text-sm">
              <Info label="Prop firm">{cicloScelto.propFirm.name}</Info>
              <Info label="Fase che aspetta">{PHASE_LABEL(cicloScelto.awaitingPropPhase)}</Info>
              <Info label={LABELS.terms.recoverAmount}>{money(cicloScelto.recoverAmount, cicloScelto.currency)}</Info>
              <Info label="Simbolo">{cicloScelto.lastPhase?.canonicalSymbol ?? "—"}</Info>
              <p className="col-span-2 text-xs text-faint">
                Simbolo, DD e target si ricopiano dall'ultima fase: continuano la stessa coppia.
              </p>
            </dl>
          )}

          {!nuovaChallenge && faseScelta && (
            <dl className="grid grid-cols-2 gap-3 rounded-card border border-line bg-surface p-4 text-sm">
              <Info label="Prop firm">{faseScelta.preset ? `${faseScelta.preset.propFirm.name} · ${faseScelta.preset.name}` : "—"}</Info>
              <Info label="Fase che aspetta">{PHASE_LABEL(faseScelta.awaitingPropPhase)}</Info>
              <Info label="Simbolo">{faseScelta.canonicalSymbol}</Info>
              <Info label="Contabilità">Nessuna</Info>
              <p className="col-span-2 text-xs text-faint">
                Programma, taglia, DD e simbolo si ricopiano dalla fase di prima. Dopo un conto bruciato riparte la Fase 1: una challenge nuova.
              </p>
            </dl>
          )}

          {nuovaChallenge && (
            <>
              <div role="radiogroup" aria-label="Fase" className="grid grid-cols-3 gap-2">
                {PHASES.map((p) => {
                  const disabled = p === "PHASE_2" && !phase2Available;
                  return (
                    <button
                      key={p}
                      type="button"
                      role="radio"
                      aria-checked={phase === p}
                      disabled={disabled}
                      title={disabled ? "Questo programma ha un solo step: non c'è una Fase 2." : undefined}
                      onClick={() => setPhaseChoice(p)}
                      className={`rounded border px-2 py-1.5 text-xs disabled:cursor-not-allowed disabled:opacity-40 ${phase === p ? "border-accent bg-raised font-medium text-ink" : "border-line text-muted hover:text-ink"}`}
                    >
                      {PHASE_LABEL(p)}
                    </button>
                  );
                })}
              </div>
              <Field label="Programma" hint={!effFirmId ? "Scegli prima la prop firm, in alto." : undefined}>
                {(id) => (
                  <select id={id} value={presetId} disabled={!effFirmId} onChange={(e) => setPresetId(e.target.value)} className={inputClass}>
                    <option value="" disabled>
                      {!effFirmId ? "Scegli prima la prop firm" : programmi.length ? "Scegli il programma" : "Nessun programma per questa prop firm"}
                    </option>
                    {programmi.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} · {p.phases === 0 ? "instant" : p.phases === 1 ? "one-step" : "two-step"}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <NumField label="Taglia del conto" value={accountSize} onChange={setAccountSize} suffix={propCurrency} />
                {accounting && <NumField label="Fee pagata" value={fee} onChange={setFee} suffix={propCurrency} hint={preset && !(Number(preset.challengeFee) > 0) ? "Il programma non dichiara la fee: scrivi quella pagata." : undefined} />}
                {declaresP1 && (
                  <NumField
                    label={phase === "PHASE_2" ? "Perdita sul broker in Fase 1" : "Perdita in Fase 1"}
                    check="Perdita in Fase 1"
                    value={lossP1}
                    onChange={setLossP1}
                    suffix={propCurrency}
                    hint="Fatta fuori dal gestionale. 0 se non c'è stata."
                  />
                )}
                {declaresP2 && <NumField label="Perdita in Fase 2" value={lossP2} onChange={setLossP2} suffix={propCurrency} hint="Fatta fuori dal gestionale. 0 se non c'è stata." />}
                <NumField label="Max drawdown" value={maxDdPct} onChange={setMaxDdPct} suffix="%" />
                <Field label="Base del DD">
                  {(id) => (
                    <select id={id} value={basis} onChange={(e) => setBasis(e.target.value as DdBasis)} className={inputClass}>
                      {BASES.map((b) => (
                        <option key={b} value={b}>
                          {LABELS.ddBasis[b]}
                        </option>
                      ))}
                    </select>
                  )}
                </Field>
              </div>
              {declaresP1 && (
                <Field label="Nota sulle perdite" hint="Facoltativa: da dove vengono gli importi.">
                  {(id) => <input id={id} value={lossNote} onChange={(e) => setLossNote(e.target.value)} className={inputClass} />}
                </Field>
              )}
              {accounting && (
                <Field label="Etichetta del ciclo" hint="Facoltativa, per riconoscerlo nello storico.">
                  {(id) => <input id={id} value={cycleLabel} onChange={(e) => setCycleLabel(e.target.value)} className={inputClass} />}
                </Field>
              )}
              <div className="grid grid-cols-2 gap-3">
                <NumField label="DD giornaliero" value={dailyDdPct} onChange={setDailyDdPct} suffix="%" hint="Vuoto se non previsto." />
                <NumField label={`Target ${PHASE_LABEL(phase).toLowerCase()}`} check="Target" value={targetPct} onChange={setTargetPct} suffix="%" />
              </div>
              <Field label="Simbolo" hint={
                  !propServer
                    ? "Scegli prima il server del conto prop."
                    : !symbolOptions.length
                      ? canReadSymbols
                        ? "Nessun simbolo mappato su questo server: leggili dal conto col pulsante qui sotto, oppure «+ Mappa simbolo…»."
                        : "Nessun simbolo in comune: «+ Mappa simbolo…» qui nel menu."
                      : undefined
                }>
                {(id) => (
                  <select
                    id={id}
                    value={symbol}
                    onChange={(e) => {
                      // Le voci «+ Mappa…» aprono il form: non sono un simbolo, e il valore scelto resta quello di prima.
                      if (e.target.value === MAPPA_PROP) setMapOn(propServer ?? null);
                      else if (e.target.value === MAPPA_BROKER) setMapOn(brokerServerForSymbols ?? null);
                      else setSymbol(e.target.value);
                    }}
                    disabled={!propServer}
                    className={inputClass}
                  >
                    <option value="" disabled>
                      {symbolOptions.length ? "Scegli il simbolo" : "Nessun simbolo mappato"}
                    </option>
                    {symbolOptions.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                    {brokerServerForSymbols ? (
                      <>
                        <option value={MAPPA_PROP}>+ Mappa simbolo sul server prop…</option>
                        <option value={MAPPA_BROKER}>+ Mappa simbolo sul server broker…</option>
                      </>
                    ) : (
                      <option value={MAPPA_PROP}>+ Mappa simbolo…</option>
                    )}
                  </select>
                )}
              </Field>
              {canReadSymbols && (
                <div className="space-y-2">
                  <Button onClick={() => void readSymbols()} disabled={readingSymbols || busy} aria-busy={readingSymbols}>
                    {readingSymbols ? "Lettura in corso…" : "Leggi simboli dal conto"}
                  </Button>
                  {!conflict && <ErrorNote>{readSymbolsError}</ErrorNote>}
                </div>
              )}
            </>
          )}
        </section>

        <section className="space-y-3 border-t border-line pt-4">
          <h3 className="text-sm font-medium text-ink">Il broker</h3>
          <div role="radiogroup" aria-label="Conto broker" className="grid grid-cols-3 gap-2">
            {(
              [
                ["EXISTING", "Libero fra i miei"],
                ["NEW", "Nuovo"],
                ["NONE", "Nessuno"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={brokerChoice === value}
                onClick={() => setBrokerChoice(value)}
                className={`rounded border px-2 py-1.5 text-xs ${brokerChoice === value ? "border-accent bg-raised font-medium text-ink" : "border-line text-muted hover:text-ink"}`}
              >
                {label}
              </button>
            ))}
          </div>

          {brokerChoice === "EXISTING" && (
            <Field label="Conto broker" hint={!brokerLiberi.length ? "Nessun conto broker libero: creane uno nuovo, o lascia vuoto e aggiungilo dopo." : undefined}>
              {(id) => (
                <select id={id} value={brokerAccountId} onChange={(e) => setBrokerAccountId(e.target.value)} disabled={!brokerLiberi.length} className={inputClass}>
                  <option value="" disabled>
                    {brokerLiberi.length ? "Scegli il conto" : "Nessun conto libero"}
                  </option>
                  {brokerLiberi.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.label} · {a.mt5Login}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          )}

          {brokerChoice === "NEW" && (
            <NewBrokerFields
              brokers={brokers.data ?? []}
              brokerId={brokerOwnerId}
              onBrokerId={setBrokerOwnerId}
              servers={brokerServers}
              login={newBrokerLogin}
              onLogin={setNewBrokerLogin}
              password={newBrokerPassword}
              onPassword={setNewBrokerPassword}
              serverId={newBrokerServerId}
              onServerId={setNewBrokerServerId}
              currency={newBrokerCurrency}
              onCurrency={setNewBrokerCurrency}
              label={newBrokerLabel}
              onLabel={setNewBrokerLabel}
            />
          )}

          {brokerChoice === "NONE" && (
            <p className="rounded-card border border-line bg-surface p-3 text-xs text-muted">
              La fase nasce lo stesso, in attesa del conto broker: non si apre niente finché non arriva. Si aggancia più
              tardi dalla scheda della fase.
            </p>
          )}
        </section>
        </>
        )}

        {conflict && (error ?? readSymbolsError) ? (
          <AccountConflictNote message={(error ?? readSymbolsError)!} conflict={conflict} onOpen={onClose} />
        ) : (
          <ErrorNote>{error}</ErrorNote>
        )}
        {errorCode === "BALANCE_NOT_READ" &&
          (nuovaChallenge ? (
            <p className="text-xs text-warn">
              Il saldo del conto broker non si è letto: la fase non è nata, e i conti appena creati sono stati annullati (li
              trovi fra gli Eliminati). Riprova quando il conto risponde.
            </p>
          ) : (
            <p className="text-xs text-warn">
              Il saldo del conto broker non si è letto: il conto prop non è stato creato. Un conto broker nuovo invece sì, ed
              è fra i liberi — riprova scegliendolo da «Libero fra i miei».
            </p>
          ))}
      </div>
      </FormCheck>
    </SidePanel>
    {open && mapOn && <SymbolPanel server={mapOn} symbol={null} peersOf={peersOf} onClose={() => setMapOn(null)} />}
    </>
  );
}

/** Le voci del menu Simbolo che aprono il form di mappatura, invece di scegliere. */
const MAPPA_PROP = "__mappa_prop__";
const MAPPA_BROKER = "__mappa_broker__";

/**
 * I campi di un conto broker nuovo: gli stessi nel percorso prop («Il broker →
 * Nuovo») e nel conto broker da solo. Si sceglie PRIMA il broker e poi il server,
 * con quelli già legati a quel broker in cima.
 */
function NewBrokerFields({
  brokers,
  brokerId,
  onBrokerId,
  servers,
  login,
  onLogin,
  password,
  onPassword,
  serverId,
  onServerId,
  currency,
  onCurrency,
  label,
  onLabel,
}: {
  brokers: PropFirm[];
  brokerId: string;
  onBrokerId: (v: string) => void;
  servers: BrokerServer[];
  login: string;
  onLogin: (v: string) => void;
  password: string;
  onPassword: (v: string) => void;
  serverId: string;
  onServerId: (v: string) => void;
  currency: string;
  onCurrency: (v: string) => void;
  label: string;
  onLabel: (v: string) => void;
}) {
  return (
    <CheckScope name={BROKER_SCOPE}>
      <Field label="Login MT5">{(id) => <input id={id} value={login} onChange={(e) => onLogin(e.target.value)} className={inputClass} />}</Field>
      <Field label="Password MT5">{(id) => <input id={id} type="password" value={password} onChange={(e) => onPassword(e.target.value)} className={inputClass} />}</Field>
      <OwnerServerFields kind="BROKER" owners={brokers} ownerId={brokerId} onOwnerId={onBrokerId} servers={servers} serverId={serverId} onServerId={onServerId}>
        <Field label="Valuta" hint="Verificata alla connessione: se il server ne dice un'altra, un conto senza storico si corregge da solo.">{(id) => <input id={id} value={currency} maxLength={3} onChange={(e) => onCurrency(e.target.value.toUpperCase())} className={inputClass} />}</Field>
      </OwnerServerFields>
      <Field label="Etichetta" hint="Facoltativa: se vuota, broker + il login.">
        {(id) => <input id={id} value={label} onChange={(e) => onLabel(e.target.value)} className={inputClass} />}
      </Field>
    </CheckScope>
  );
}

/** L'ambito dei campi del conto broker nuovo, accanto a quelli del prop nello stesso pannello. */
const BROKER_SCOPE = "conto broker";

const NUOVO_SERVER = "__nuovo__";

/**
 * PROP FIRM (o BROKER) E POI SERVER, in quest'ordine.
 *
 * Il server dice chi possiede il conto: scelto prima il possessore, il menu
 * mostra **solo i server legati a lui**, del tipo giusto (la lista arriva già
 * senza archiviati né eliminati). Finché il possessore non c'è il server non si
 * sceglie, e cambiandolo il server si azzera — l'ordine è la struttura, non un
 * consiglio.
 *
 * «Server nuovo…» crea il server **già legato** al possessore scelto
 * (`POST /servers` con `propFirmId`), così la volta dopo è fra i suoi. Il nome
 * deve essere quello esatto del terminale MT5: il backend lo verifica col
 * gateway e, se non lo trova, lo rifiuta e dice perché. I simboli di un server
 * nuovo si mappano da soli alla prima connessione.
 */
function OwnerServerFields({
  kind,
  owners,
  ownerId,
  onOwnerId,
  ownerLocked = false,
  servers,
  serverId,
  onServerId,
  children,
}: {
  kind: "PROP" | "BROKER";
  owners: PropFirm[];
  ownerId: string;
  onOwnerId: (v: string) => void;
  /** Continuando una Fase che aspetta il conto la prop firm è la sua. */
  ownerLocked?: boolean;
  servers: BrokerServer[];
  serverId: string;
  onServerId: (v: string) => void;
  /** Accanto al server: la valuta. */
  children?: ReactNode;
}) {
  const refresh = useRefreshDirectory();
  const [nuovo, setNuovo] = useState(false);
  const [nome, setNome] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isProp = kind === "PROP";
  const ownerLabel = isProp ? "Prop firm" : "Broker";
  const owner = owners.find((o) => o.id === ownerId) ?? null;
  const legati = servers.filter((s) => ownerId !== "" && s.kind === kind && s.propFirmId === ownerId);
  const nomeServer = (s: BrokerServer) => s.displayName ?? s.name;

  const crea = async () => {
    if (!nome.trim() || !ownerId) return;
    setBusy(true);
    setError(null);
    try {
      const creato = await api.servers.create({ name: nome.trim(), kind, propFirmId: ownerId });
      await refresh();
      onServerId(creato.id);
      setNuovo(false);
      setNome("");
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Field label={ownerLabel}>
        {(id) => (
          <select
            id={id}
            value={ownerId}
            disabled={ownerLocked}
            onChange={(e) => {
              onOwnerId(e.target.value);
              // Il server di un altro possessore non resta scelto di nascosto.
              onServerId("");
              setNuovo(false);
            }}
            className={inputClass}
          >
            <option value="" disabled>
              {owners.length ? `Scegli ${isProp ? "la prop firm" : "il broker"}` : isProp ? "Nessuna prop firm nel catalogo" : "Nessun broker nel catalogo"}
            </option>
            {owners.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        )}
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Server">
          {(id) => (
            <select
              id={id}
              value={nuovo ? NUOVO_SERVER : serverId}
              disabled={!ownerId}
              onChange={(e) => {
                if (e.target.value === NUOVO_SERVER) setNuovo(true);
                else {
                  setNuovo(false);
                  onServerId(e.target.value);
                }
              }}
              className={inputClass}
            >
              <option value="" disabled>
                {!ownerId ? `Scegli prima ${isProp ? "la prop firm" : "il broker"}` : legati.length ? "Scegli il server" : `Nessun server di ${owner?.name ?? ownerLabel}: creane uno`}
              </option>
              {legati.map((s) => (
                <option key={s.id} value={s.id}>
                  {nomeServer(s)}
                </option>
              ))}
              {ownerId && <option value={NUOVO_SERVER}>+ Server nuovo…</option>}
            </select>
          )}
        </Field>
        {children}
      </div>
      {nuovo && ownerId && (
        <div className="space-y-2 rounded-card border border-line bg-surface p-3">
          <Field label="Nome del server" hint={`Identico a quello del terminale MT5. Nasce legato a ${owner?.name ?? ownerLabel}.`}>
            {(id) => <input id={id} value={nome} onChange={(e) => setNome(e.target.value)} className={inputClass} />}
          </Field>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="primary" disabled={busy || !nome.trim()} onClick={() => void crea()}>
              {busy ? "Verifico sul gateway…" : "Crea server"}
            </Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => setNuovo(false)}>
              Annulla
            </Button>
          </div>
          <ErrorNote>{error}</ErrorNote>
        </div>
      )}
    </>
  );
}

function NumField({ label, check, value, onChange, suffix, hint }: { label: string; check?: string; value: string; onChange: (v: string) => void; suffix?: string; hint?: string }) {
  return (
    <Field label={label} hint={hint} check={check}>
      {(id) => (
        <div className="relative">
          <input id={id} inputMode="decimal" value={value} onChange={(e) => onChange(e.target.value)} className={`${inputClass} ${suffix ? "pr-10" : ""}`} />
          {suffix && <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-faint">{suffix}</span>}
        </div>
      )}
    </Field>
  );
}

function Info({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-faint">{label}</dt>
      <dd className="mt-0.5 text-ink">{children}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Il broker arriva dopo
// ---------------------------------------------------------------------------

/**
 * AGGANCIA IL BROKER a una Fase `AWAITING_BROKER`: libero fra i conti del
 * cliente, o nuovo. Un gesto che succede una volta sola — da qui in poi la
 * Fase si comporta come una nata con broker fin dall'inizio.
 */
export function AttachBrokerPanel({
  open,
  sessionId,
  clientId,
  clientName,
  brokerAccounts,
  onClose,
  mode = "attach",
}: {
  open: boolean;
  sessionId: string;
  clientId: string;
  clientName: string;
  brokerAccounts: Account[];
  onClose: () => void;
  /**
   * `replace`: «Sostituisci conto broker» su una Fase viva. Stessa scelta (libero
   * o nuovo) e stessi controlli; niente base a mano (servono i due saldi letti),
   * e il tasso broker -> ciclo quando la chiusura del periodo lo chiede.
   */
  mode?: "attach" | "replace";
}) {
  const replace = mode === "replace";
  const verbo = replace ? "Sostituisci" : "Aggancia";
  const refresh = useRefreshDirectory();
  const servers = useServers();
  const brokers = useBrokers();
  const session = useSession(sessionId);
  const brokerServers = (servers.data ?? []).filter((s) => s.kind === "BROKER");
  const brokerLiberi = brokerAccounts.filter((a) => !a.liveSession);

  const [choice, setChoice] = useState<"EXISTING" | "NEW">(brokerLiberi.length ? "EXISTING" : "NEW");
  const [brokerAccountId, setBrokerAccountId] = useState(brokerLiberi[0]?.id ?? "");
  const [mt5Login, setMt5Login] = useState("");
  const [mt5Password, setMt5Password] = useState("");
  const [brokerOwnerId, setBrokerOwnerId] = useState("");
  const [serverId, setServerId] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [label, setLabel] = useState("");
  const [baseAmount, setBaseAmount] = useState("");
  const [baseNote, setBaseNote] = useState("");
  const [fxRate, setFxRate] = useState("");
  /*
   * IL SALDO FINALE DEL BROKER DI PRIMA (12/10): solo se la Fase è tornata da un
   * ripristino senza di lui e il suo periodo è ancora aperto (`orphanPeriod`).
   * Parte dall'ultimo saldo letto, si può cambiare, e si salva DICHIARATO col
   * motivo: non è una lettura di adesso.
   */
  const orfano = replace ? null : (session.data?.orphanPeriod ?? null);
  const [oldAmount, setOldAmount] = useState("");
  const [oldNote, setOldNote] = useState("");
  useEffect(() => {
    if (orfano?.lastBalance != null) setOldAmount((v) => (v === "" ? String(orfano.lastBalance) : v));
  }, [orfano?.periodId, orfano?.lastBalance]);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  // Il conto che tiene un numero MT5 gia' usato (409 LOGIN_TAKEN): `AccountConflictNote`.
  const [conflict, setConflict] = useState<AccountConflict | null>(null);

  // Lo stesso controllo del tasso del payout: vuoto, o un numero maggiore di zero.
  const rateOk = !fxRate.trim() || Number(decimalInput(fxRate) ?? 0) > 0;
  const mb = manualBase(baseAmount, baseNote);
  const check = useFormCheck([
    choice === "EXISTING" && ["Conto broker", !!brokerAccountId],
    choice === "NEW" && ["Login MT5", !!mt5Login.trim()],
    choice === "NEW" && ["Password MT5", !!mt5Password],
    choice === "NEW" && ["Broker", !!brokerOwnerId],
    choice === "NEW" && ["Server", !!serverId],
    choice === "NEW" && ["Valuta", currency.trim().length === 3],
    !!orfano && ["Saldo finale", decimalInput(oldAmount) !== null],
    !!orfano && ["Motivo del saldo dichiarato", oldNote.trim().length >= 3],
    ["Tasso", rateOk],
    !replace && ["Base del primo periodo", !mb.fieldError],
    !replace && ["Motivo della base", !mb.issue],
  ]);
  const fieldsOf: Record<string, string> = { accountId: "Conto broker", mt5Login: "Login MT5", LOGIN_TAKEN: "Login MT5", mt5Password: "Password MT5", serverId: "Server", currency: "Valuta", fxRate: "Tasso", baseAmount: "Base del primo periodo", baseNote: "Motivo della base", "oldBrokerClose.amount": "Saldo finale", "oldBrokerClose.note": "Motivo del saldo dichiarato", "oldBrokerClose.fxRate": "Tasso" };

  const submit = async () => {
    if (!check.attempt()) return;
    const scelta =
      choice === "EXISTING"
        ? { accountId: brokerAccountId }
        : { mt5Login: mt5Login.trim(), mt5Password, serverId, label: label.trim() || null, currency: currency.trim().toUpperCase() };
    if (replace) {
      const rate = fxRate.trim() ? decimalInput(fxRate) : null;
      setBusy(true);
      setError(null);
      setErrorCode(null);
      setConflict(null);
      try {
        showSymbolWarnings(await api.sessions.replaceBroker(sessionId, { ...scelta, ...(rate ? { fxRate: rate } : {}) }));
        await refresh();
        onClose();
      } catch (e) {
        setError(errorMessage(e));
        setErrorCode(e instanceof ApiError ? e.code : null);
        if (e instanceof ApiError && e.conflict) setConflict(e.conflict);
        check.fromError(e, fieldsOf);
      } finally {
        setBusy(false);
      }
      return;
    }
    const { base } = mb;
    const oldRate = orfano && fxRate.trim() ? decimalInput(fxRate) : null;
    setBusy(true);
    setError(null);
    setErrorCode(null);
    setConflict(null);
    try {
      const r = await api.sessions.attachBroker(sessionId, {
        ...(choice === "EXISTING" ? { accountId: brokerAccountId } : { mt5Login: mt5Login.trim(), mt5Password, serverId, label: label.trim() || null, currency: currency.trim().toUpperCase() }),
        // Il numero col punto, come in «Crea fase» ("0,5" e "0.5" valgono uguale).
        ...(base !== null ? { baseAmount: base, baseNote: baseNote.trim() } : {}),
        ...(orfano ? { oldBrokerClose: { amount: decimalInput(oldAmount)!, note: oldNote.trim(), ...(oldRate ? { fxRate: oldRate } : {}) } } : {}),
      });
      showSymbolWarnings(r);
      await refresh();
      onClose();
    } catch (e) {
      setError(errorMessage(e));
      setErrorCode(e instanceof ApiError ? e.code : null);
      if (e instanceof ApiError && e.conflict) setConflict(e.conflict);
      check.fromError(e, fieldsOf);
    } finally {
      setBusy(false);
    }
  };

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title={replace ? "Sostituisci conto broker" : session.data?.kind === "BB" ? "Aggiungi broker B" : "Aggiungi conto broker"}
      subtitle={replace && session.data?.brokerAccount ? `${clientName} · al posto di ${session.data.brokerAccount.label}` : clientName}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Annulla
          </Button>
          <Button variant="primary" disabled={busy} onClick={() => void submit()}>
            {busy ? (replace ? "Sostituzione…" : "Aggancio…") : verbo}
          </Button>
          <MissingNote check={check} end />
        </>
      }
    >
      <FormCheck check={check}>
      <div className="space-y-4">
        {replace && (
          <p className="rounded border border-line bg-raised/50 p-3 text-sm text-muted">
            Solo senza coppie aperte. Il conto di adesso torna fra i liberi del cliente. Con la contabilità il periodo del
            broker attuale si chiude sul suo saldo letto e se ne apre uno sul saldo letto del nuovo: se uno dei due non si
            legge, non si sostituisce niente.
          </p>
        )}
        <div role="radiogroup" aria-label="Conto broker" className="grid grid-cols-2 gap-2">
          {(
            [
              ["EXISTING", "Libero fra i miei"],
              ["NEW", "Nuovo"],
            ] as const
          ).map(([value, l]) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={choice === value}
              onClick={() => setChoice(value)}
              className={`rounded border px-2 py-1.5 text-xs ${choice === value ? "border-accent bg-raised font-medium text-ink" : "border-line text-muted hover:text-ink"}`}
            >
              {l}
            </button>
          ))}
        </div>

        {choice === "EXISTING" ? (
          <Field label="Conto broker" hint={!brokerLiberi.length ? "Nessun conto libero: creane uno nuovo." : undefined}>
            {(id) => (
              <select id={id} value={brokerAccountId} onChange={(e) => setBrokerAccountId(e.target.value)} disabled={!brokerLiberi.length} className={inputClass}>
                <option value="" disabled>
                  {brokerLiberi.length ? "Scegli il conto" : "Nessun conto libero"}
                </option>
                {brokerLiberi.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.label} · {a.mt5Login}
                  </option>
                ))}
              </select>
            )}
          </Field>
        ) : (
          <>
            <Field label="Login MT5">{(id) => <input id={id} value={mt5Login} onChange={(e) => setMt5Login(e.target.value)} className={inputClass} />}</Field>
            <Field label="Password MT5">{(id) => <input id={id} type="password" value={mt5Password} onChange={(e) => setMt5Password(e.target.value)} className={inputClass} />}</Field>
            <OwnerServerFields kind="BROKER" owners={brokers.data ?? []} ownerId={brokerOwnerId} onOwnerId={setBrokerOwnerId} servers={brokerServers} serverId={serverId} onServerId={setServerId}>
              <Field label="Valuta" hint="Verificata alla connessione: se il server ne dice un'altra, un conto senza storico si corregge da solo.">{(id) => <input id={id} value={currency} maxLength={3} onChange={(e) => setCurrency(e.target.value.toUpperCase())} className={inputClass} />}</Field>
            </OwnerServerFields>
            <Field label="Etichetta" hint="Facoltativa: se vuota, broker + il login.">
              {(id) => <input id={id} value={label} onChange={(e) => setLabel(e.target.value)} className={inputClass} />}
            </Field>
          </>
        )}

        {orfano && (
          <div className="space-y-3 rounded border border-line bg-raised/50 p-3">
            <p className="text-sm text-muted">
              Il periodo n. {orfano.seq} del broker di prima, {orfano.broker.label} <span className="font-mono">{orfano.broker.mt5Login}</span>, è ancora
              aperto{orfano.broker.deleted ? " e il conto è fra gli eliminati" : ""}: il suo saldo non si legge più. Indica il saldo finale: si salva come
              dichiarato, col motivo, e chiude il periodo prima di aprire quello del nuovo.
            </p>
            <Field
              label={`Saldo finale di ${orfano.broker.label} (${orfano.currency})`}
              check="Saldo finale"
              hint={
                orfano.lastBalance !== null && orfano.lastBalanceAt
                  ? `Proposto l'ultimo saldo letto, ${money(orfano.lastBalance, orfano.currency)} il ${dateTime(orfano.lastBalanceAt)}: puoi cambiarlo. Base del periodo ${money(orfano.baseAmount, orfano.currency)}.`
                  : `Nessun saldo letto da proporre. Base del periodo ${money(orfano.baseAmount, orfano.currency)}.`
              }
            >
              {(id) => <input id={id} inputMode="decimal" value={oldAmount} onChange={(e) => setOldAmount(e.target.value)} className={inputClass} />}
            </Field>
            <Field label="Motivo del saldo dichiarato" hint="Obbligatorio: da dove viene questo numero (per esempio: letto dal terminale MT5 il …).">
              {(id) => <input id={id} value={oldNote} onChange={(e) => setOldNote(e.target.value)} className={inputClass} />}
            </Field>
            {errorCode === "FX_RATE_MISSING" && (
              <Field
                label={`Tasso ${orfano.currency}/${orfano.cycleCurrency}`}
                check="Tasso"
                hint="Né feed del broker né BCE: scrivi il tasso da congelare sulla chiusura del periodo, poi conferma di nuovo."
              >
                {(id) => <input id={id} inputMode="decimal" value={fxRate} placeholder="es. 1.0850" onChange={(e) => setFxRate(e.target.value)} className={inputClass} />}
              </Field>
            )}
          </div>
        )}
        {conflict && error ? <AccountConflictNote message={error} conflict={conflict} onOpen={onClose} /> : <ErrorNote>{error}</ErrorNote>}
        {replace && (errorCode === "FX_RATE_MISSING" || /[Tt]asso .* non disponibile/.test(error ?? "")) && (
          <Field
            label={`Tasso ${session.data?.brokerAccount?.currency ?? "broker"}/${session.data?.cycle?.currency ?? "ciclo"}`}
            check="Tasso"
            hint="Né feed del broker né BCE: scrivi il tasso da congelare sulla chiusura del periodo, poi conferma di nuovo."
          >
            {(id) => <input id={id} inputMode="decimal" value={fxRate} placeholder="es. 1.0850" onChange={(e) => setFxRate(e.target.value)} className={inputClass} />}
          </Field>
        )}
        {!replace && errorCode === "BALANCE_NOT_READ" && (
          <ManualBaseFields currency={currency} amount={baseAmount} onAmount={setBaseAmount} note={baseNote} onNote={setBaseNote} />
        )}
      </div>
      </FormCheck>
    </SidePanel>
  );
}

// ---------------------------------------------------------------------------
// «Crea ciclo»: la contabilità che nasce dopo
// ---------------------------------------------------------------------------

/** Perché «Crea ciclo» non c'è su una Fase 2 o Funded: lo stesso testo che dà il backend. */
export const CREATE_CYCLE_ONLY_PHASE_1 =
  "Si può creare il ciclo solo da una Fase 1: per Fase 2 e Funded mancano fee e periodi dei tratti precedenti, e non si possono ricostruire.";

/**
 * «Crea ciclo» su una Fase che opera senza. Il ciclo nasce adesso e i suoi
 * numeri partono da adesso; con un broker agganciato il primo periodo parte dal
 * saldo letto in questo momento, e se non si legge non nasce niente.
 *
 * `variant="menu"` per i menu «…» delle liste, `"button"` per l'intestazione
 * della Fase. Su una Fase 2 o Funded la voce c'è ma è disabilitata, col motivo.
 */
export function CreateCycleAction({
  session,
  variant = "menu",
}: {
  session: { id: string; phase: CyclePhase; cycle: unknown };
  variant?: "menu" | "button";
}) {
  const [open, setOpen] = useState(false);
  if (session.cycle) return null;
  const disabled = session.phase !== "PHASE_1";
  return (
    <>
      {variant === "menu" ? (
        <button
          type="button"
          role="menuitem"
          disabled={disabled}
          title={disabled ? CREATE_CYCLE_ONLY_PHASE_1 : undefined}
          onClick={() => setOpen(true)}
          className="block w-full rounded px-2 py-1.5 text-left text-sm text-ink hover:bg-raised disabled:cursor-not-allowed disabled:text-faint disabled:hover:bg-transparent"
        >
          Crea ciclo
        </button>
      ) : (
        <Button disabled={disabled} title={disabled ? CREATE_CYCLE_ONLY_PHASE_1 : undefined} onClick={() => setOpen(true)}>
          Crea ciclo
        </Button>
      )}
      {open && <CreateCyclePanel sessionId={session.id} onClose={() => setOpen(false)} />}
    </>
  );
}

function CreateCyclePanel({ sessionId, onClose }: { sessionId: string; onClose: () => void }) {
  const navigate = useNavigate();
  const refresh = useRefreshDirectory();
  const [label, setLabel] = useState("");
  const [fee, setFee] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  /*
   * LA FEE DEL PROGRAMMA VALE COME PREDEFINITO SOLO SE E' MAGGIORE DI ZERO.
   * Nel catalogo zero vuol dire «non dichiarata»: prenderlo farebbe partire il
   * ciclo come una challenge gratis, e il «Da recuperare» sarebbe sbagliato.
   * Senza un programma noto (o finché la Fase si carica) la fee e' obbligatoria.
   * Uno zero scritto a mano resta valido: e' una dichiarazione.
   */
  const session = useSession(sessionId);
  const programFee = Number(session.data?.preset?.challengeFee ?? 0);
  const feeRequired = !(programFee > 0);
  const feeValue = fee.trim() ? decimalInput(fee) : null;
  // Scritta, un numero; vuota, solo se il programma la dichiara.
  const check = useFormCheck([["Fee pagata", fee.trim() ? feeValue !== null : !feeRequired]]);

  const submit = async () => {
    if (!check.attempt()) return;
    setBusy(true);
    setError(null);
    setErrorCode(null);
    try {
      await api.sessions.createCycle(sessionId, {
        ...(label.trim() ? { cycleLabel: label.trim() } : {}),
        ...(feeValue !== null ? { challengeFee: feeValue } : {}),
      });
      await refresh();
      onClose();
      navigate(`/fasi/${sessionId}`);
    } catch (e) {
      setError(errorMessage(e));
      setErrorCode(e instanceof ApiError ? e.code : null);
      check.fromError(e, { challengeFee: "Fee pagata", cycleLabel: "Etichetta del ciclo" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <SidePanel
      open
      onClose={onClose}
      title="Crea ciclo"
      subtitle="La contabilità parte adesso"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Annulla
          </Button>
          <Button variant="primary" disabled={busy} onClick={() => void submit()}>
            {busy ? "Creazione…" : "Crea ciclo"}
          </Button>
          <MissingNote check={check} end />
        </>
      }
    >
      <FormCheck check={check}>
      <div className="space-y-4">
        <p className="rounded-card border border-line bg-surface p-3 text-sm text-muted">
          Il ciclo nasce adesso e i suoi numeri partono da adesso: il costo recuperato da operazioni precedenti non gli appartiene e non
          viene contato. La fee entra nel registro; il primo periodo parte dal saldo del broker <span className="text-ink">letto in questo momento</span>.
          Se il saldo non si legge, non nasce niente.
        </p>
        <Field
          label="Fee pagata"
          hint={feeRequired ? "Obbligatoria: il programma non dichiara la fee, e uno zero preso da lì sarebbe una challenge gratis." : `Vuoto = quella del programma (${money(programFee, session.data?.propAccount.currency ?? "USD")}).`}
        >
          {(id) => <input id={id} inputMode="decimal" value={fee} onChange={(e) => setFee(e.target.value)} className={inputClass} />}
        </Field>
        <Field label="Etichetta del ciclo" hint="Facoltativa, per riconoscerlo nello storico.">
          {(id) => <input id={id} value={label} onChange={(e) => setLabel(e.target.value)} className={inputClass} />}
        </Field>
        <ErrorNote>{error}</ErrorNote>
        {errorCode === "PAIRS_OPEN" && <p className="text-xs text-warn">Chiudi le operazioni aperte, poi riprova.</p>}
        {errorCode === "BASE_UNREAD" && <p className="text-xs text-warn">Il conto broker non ha risposto: riprova quando è raggiungibile.</p>}
      </div>
      </FormCheck>
    </SidePanel>
  );
}

/**
 * LA BASE DEL PRIMO PERIODO A MANO, quando il saldo del broker non si e' letto
 * (`BALANCE_NOT_READ`). Un componente e una regola soli per «Aggiungi conto
 * broker» e «Crea fase»: un numero, e il motivo obbligatorio. Il backend la usa
 * solo se il saldo continua a non leggersi, e la scrive nell'audit della
 * nascita col motivo (`baseSource: MANUAL`).
 */
export function ManualBaseFields({
  currency,
  amount,
  onAmount,
  note,
  onNote,
}: {
  currency: string;
  amount: string;
  onAmount: (v: string) => void;
  note: string;
  onNote: (v: string) => void;
}) {
  return (
    <div className="space-y-3 rounded-card border border-warn/40 bg-warn/5 p-3">
      <p className="text-xs text-ink">Il saldo non si è letto: dichiara la base del primo periodo, col motivo.</p>
      {/* Virgola o punto; il resto e' un errore scritto sotto il campo (`parseDecimal`). */}
      <DecimalField label={`Base del primo periodo (${currency})`} check="Base del primo periodo" value={amount} onChange={onAmount} />
      <Field label="Motivo della base">{(id) => <input id={id} value={note} onChange={(e) => onNote(e.target.value)} className={inputClass} />}</Field>
    </div>
  );
}

/**
 * Le regole della base a mano: vuota = nessuna base; scritta = un numero (virgola
 * o punto, `parseDecimal`), col motivo. `base` e' il numero col punto, quello da
 * mandare. Un testo che non e' un numero ferma l'invio (`fieldError`) e lo dice
 * il campo stesso (`DecimalField`): non si ripete nel riquadro del pannello.
 */
export function manualBase(amount: string, note: string): { base: string | null; fieldError: boolean; issue: string | null } {
  const p = parseDecimal(amount);
  if (p.kind === "error") return { base: null, fieldError: true, issue: null };
  const base = p.kind === "value" ? p.value : null;
  if (base !== null && note.trim().length < 3) return { base, fieldError: false, issue: "Una base indicata a mano richiede il motivo" };
  return { base, fieldError: false, issue: null };
}
